/**
 * Notification Recipients — who receives BL's own internal alert emails.
 *
 * These addresses used to be constants in app/config.py
 * (CUSTOM_PLAN_INTEREST_TO / _CC), so rerouting a lead alert meant a code change
 * and a redeploy. They are rows now, and this is the only screen that edits them.
 *
 * Two rules the API enforces, mirrored in the UI so they don't surprise anyone:
 *  - An event must keep at least one active primary (To) recipient. Removing or
 *    demoting the last one is refused — an empty list silently falls back to the
 *    old hardcoded address, which is exactly what this module exists to end.
 *  - Delivery is To or CC only. The mailer's _send() has no bcc, and offering a
 *    mode that quietly does nothing would be worse than not offering it.
 */
import { useCallback, useEffect, useState } from "react";
import { AtSign, Ban, Check, Info, Loader2, MailPlus, Plus, Trash2, X } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface NotificationEvent {
  key: string;
  label: string;
  description: string;
  consequence: string;
}

interface Recipient {
  id: string;
  event_key: string;
  email: string;
  delivery: "to" | "cc";
  label: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

type FormState = {
  event_key: string;
  email: string;
  delivery: "to" | "cc";
  label: string;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function SuperAdminNotificationRecipientsPage() {
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  // Id of the row currently mid-request, so only its own button spins.
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventRows, recipientRows] = await Promise.all([
        superAdminApi.get<NotificationEvent[]>("/notification-recipients/events"),
        superAdminApi.get<Recipient[]>("/notification-recipients"),
      ]);
      setEvents(eventRows);
      setRecipients(recipientRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load notification recipients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!form || saving) return;
    if (!form.email.trim()) {
      setError("An email address is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await superAdminApi.post<Recipient>("/notification-recipients", {
        event_key: form.event_key,
        email: form.email.trim(),
        delivery: form.delivery,
        label: form.label.trim() || null,
      });
      setForm(null);
      setNotice(`${form.email.trim()} now receives this notification.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add the recipient.");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (r: Recipient, body: Partial<Pick<Recipient, "delivery" | "is_active">>) => {
    setBusyId(r.id);
    setError(null);
    setNotice(null);
    try {
      await superAdminApi.patch<Recipient>(`/notification-recipients/${r.id}`, body);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the recipient.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: Recipient) => {
    if (!window.confirm(
      `Remove ${r.email}?\n\nThey stop receiving this notification immediately. ` +
      `Nothing else is deleted — past emails and the leads themselves are unaffected.`,
    )) return;
    setBusyId(r.id);
    setError(null);
    setNotice(null);
    try {
      await superAdminApi.delete(`/notification-recipients/${r.id}`);
      setNotice(`${r.email} was removed.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove the recipient.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Platform"
        icon={MailPlus}
        title="Notification"
        titleAccent="Recipients"
        description="Who on the BL team receives each internal alert email. Add or remove an inbox here — nothing about this routing lives in code any more, so a change takes effect on the very next notification with no redeploy."
      />

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}
      {notice && (
        <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-600">
          {notice}
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 rounded-2xl border border-border/80 bg-card p-6 text-sm text-muted-foreground shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-border/80 bg-card p-10 text-center shadow-sm">
          <MailPlus className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 font-semibold text-foreground">No routable notifications</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Events are declared in code — see app/services/notification_recipients.py.
          </p>
        </div>
      ) : (
        events.map((event) => {
          const rows = recipients.filter((r) => r.event_key === event.key);
          const activePrimaries = rows.filter((r) => r.is_active && r.delivery === "to").length;
          return (
            <section
              key={event.key}
              className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/70 px-5 py-4">
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-foreground">{event.label}</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">
                    {event.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ event_key: event.key, email: "", delivery: "to", label: "" })}
                  className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-[filter] hover:brightness-105"
                >
                  <Plus className="h-4 w-4" /> Add recipient
                </button>
              </div>

              {rows.length === 0 ? (
                <div className="p-10 text-center">
                  <AtSign className="mx-auto h-8 w-8 text-muted-foreground/40" />
                  <p className="mt-3 font-semibold text-foreground">Nobody is on this list</p>
                  <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                    This notification is falling back to its built-in address. Add a recipient to take
                    control of where it goes.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border/70 bg-muted/40 text-left text-[0.65rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Recipient</th>
                        <th className="px-4 py-3" title="To = primary recipient, CC = copied">Delivery</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Added</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const busy = busyId === r.id;
                        // Blocking the last active primary in the UI too, so the
                        // button reads as unavailable instead of erroring on click.
                        const isLastPrimary = r.is_active && r.delivery === "to" && activePrimaries <= 1;
                        return (
                          <tr key={r.id} className="border-b border-border/50 last:border-0">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-foreground">{r.email}</div>
                              {r.label && (
                                <div className="text-xs text-muted-foreground">{r.label}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={r.delivery}
                                disabled={busy || isLastPrimary}
                                title={isLastPrimary
                                  ? "The only primary recipient — add another before changing this one to CC."
                                  : "To = primary recipient, CC = copied"}
                                onChange={(e) => void patch(r, { delivery: e.target.value as "to" | "cc" })}
                                className="h-8 rounded-lg border border-border/70 bg-background px-2 text-xs font-bold uppercase tracking-[0.1em] text-foreground focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <option value="to">To</option>
                                <option value="cc">CC</option>
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] ${
                                r.is_active
                                  ? "bg-emerald-500/15 text-emerald-600"
                                  : "bg-muted text-muted-foreground"
                              }`}>
                                {r.is_active ? "Receiving" : "Paused"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.created_at)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                {busy ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      disabled={isLastPrimary}
                                      title={isLastPrimary
                                        ? "The only primary recipient — add another before pausing this one."
                                        : r.is_active ? "Pause (stop sending, keep the address)" : "Resume"}
                                      onClick={() => void patch(r, { is_active: !r.is_active })}
                                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                    >
                                      {r.is_active ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isLastPrimary}
                                      title={isLastPrimary
                                        ? "The only primary recipient — add another before removing this one."
                                        : "Remove from this notification"}
                                      onClick={() => void remove(r)}
                                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="flex items-start gap-2 border-t border-border/70 bg-muted/20 px-5 py-3 text-xs leading-5 text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{event.consequence}</span>
              </p>
            </section>
          );
        })
      )}

      <p className="text-xs leading-6 text-muted-foreground">
        <strong className="text-foreground">To</strong> recipients are addressed directly.{" "}
        <strong className="text-foreground">CC</strong> recipients are copied and can see each other.{" "}
        <strong className="text-foreground">Paused</strong> keeps an address on the list without sending to
        it. Every notification must keep at least one active To recipient.
      </p>

      {form && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-bold text-foreground">Add recipient</h2>
              <button
                type="button"
                onClick={() => setForm(null)}
                aria-label="Close"
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {events.find((e) => e.key === form.event_key)?.label}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="recipient-email" className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  Email address
                </label>
                <input
                  id="recipient-email"
                  type="email"
                  autoFocus
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
                  placeholder="name@businesslocating.com"
                  className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="recipient-label" className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  Name <span className="font-medium normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  id="recipient-label"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
                  placeholder="Who this inbox belongs to"
                  className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Shown in this table only — it never appears in the email itself.
                </p>
              </div>

              <div>
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Delivery</span>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {(["to", "cc"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setForm({ ...form, delivery: mode })}
                      className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                        form.delivery === mode
                          ? "border-primary bg-primary/10"
                          : "border-border/70 bg-background hover:bg-accent"
                      }`}
                    >
                      <span className="block text-sm font-bold text-foreground">
                        {mode === "to" ? "To" : "CC"}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {mode === "to" ? "Addressed directly" : "Copied in"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setForm(null)}
                className="rounded-xl border border-border/80 px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void add()}
                disabled={saving || !form.email.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-[filter] hover:brightness-105 disabled:opacity-60"
              >
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding…</> : <><Plus className="h-4 w-4" /> Add recipient</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
